/**
 * Form di ricezione merce per un ordine.
 * Mostra le righe dell'ordine con campi per quantità ricevute/conformi/danneggiate.
 * Bottone "Chiudi ricezione" con modale conferma esito.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Combobox, type ComboboxOption, Input, Label } from '@floraltech/ui';
import { useFormatters } from '@lib/formatters';
import {
  getUltimaUO,
  setUltimaUO,
  useUnitaAttive,
} from '@lib/unitaOrganizzativa';
import { useAuth } from '@shell/AuthContext';
import { StatoRicezioneBadge } from '../components/StatoRicezioneBadge';

interface RigaRicezione {
  id: string;
  ricezione_id: string;
  riga_ordine_id: string;
  quantita_attesa: string;
  quantita_ricevuta: string;
  quantita_conforme: string;
  quantita_danneggiata: string;
  quantita_mancante: string;
  posizione_id: string | null;
  unita_organizzativa_id: string | null;
  lotto: string | null;
  scadenza_prodotto: string | null;
  esito: string;
}

interface RicezioneDetail {
  id: string;
  ordine_id: string;
  anno: number;
  numero_ddt: string | null;
  data_ricezione: string;
  stato: string;
  operatore_id: string;
  note_operatore: string | null;
  created_at: string;
  updated_at: string;
  righe: RigaRicezione[];
}

export function RicezioneForm() {
  const { ricezioneId } = useParams<{ ricezioneId: string }>();
  const navigate = useNavigate();
  const { qty } = useFormatters();
  const token = localStorage.getItem('token');
  const { user } = useAuth();

  const [ricezione, setRicezione] = useState<RicezioneDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConfirm, setShowConfirm] = useState(false);
  const [closing, setClosing] = useState(false);

  // Unita' organizzativa (negozio) di testata - obbligatoria, default ultima
  // usata, applicata a tutte le righe (stessa chiave localStorage di CaricoForm).
  const { unita, loading: loadingUnita, error: unitaFetchError } = useUnitaAttive();
  const [unitaId, setUnitaId] = useState('');
  const [unitaSearch, setUnitaSearch] = useState('');
  const [unitaPreselezionata, setUnitaPreselezionata] = useState(false);
  const [applicandoUnita, setApplicandoUnita] = useState(false);
  const [unitaError, setUnitaError] = useState<string | null>(null);

  const fetchData = async () => {
    setLoading(true);
    const res = await fetch(`/api/v1/ordini/ricezioni/${ricezioneId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) setRicezione(await res.json());
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [ricezioneId, token]);

  // Preseleziona l'ultima UO usata dall'utente (solo il campo: non scrive
  // ancora sulle righe finche' non c'e' una selezione esplicita o la chiusura).
  useEffect(() => {
    if (unitaPreselezionata || !user || unita.length === 0) return;
    const ultima = getUltimaUO(user.id);
    if (ultima && unita.some((u) => u.id === ultima)) {
      setUnitaId(ultima);
    }
    setUnitaPreselezionata(true);
  }, [user, unita, unitaPreselezionata]);

  const aggiornaRiga = async (rigaId: string, updates: Record<string, any>) => {
    await fetch(`/api/v1/ordini/ricezioni/${ricezioneId}/righe/${rigaId}`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(updates),
    });
    await fetchData();
  };

  /**
   * Applica la UO selezionata a tutte le righe che non ce l'hanno gia'
   * (evita richieste inutili). Ritorna false se una PUT fallisce (es. 422
   * UO invalida): l'errore viene mostrato vicino al selettore.
   */
  const applicaUnitaATutteLeRighe = async (
    nuovaUnitaId: string,
  ): Promise<boolean> => {
    if (!ricezione) return true;
    const righeDaAggiornare = ricezione.righe.filter(
      (r) => r.unita_organizzativa_id !== nuovaUnitaId,
    );
    if (righeDaAggiornare.length === 0) return true;

    setApplicandoUnita(true);
    setUnitaError(null);
    try {
      const risposte = await Promise.all(
        righeDaAggiornare.map((r) =>
          fetch(`/api/v1/ordini/ricezioni/${ricezioneId}/righe/${r.id}`, {
            method: 'PUT',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ unita_organizzativa_id: nuovaUnitaId }),
          }),
        ),
      );
      const fallita = risposte.find((r) => !r.ok);
      if (fallita) {
        const errData = await fallita.json().catch(() => null);
        setUnitaError(errData?.detail ?? `Errore ${fallita.status}`);
        await fetchData();
        return false;
      }
      await fetchData();
      return true;
    } catch {
      setUnitaError("Errore di rete durante l'applicazione della unita organizzativa");
      return false;
    } finally {
      setApplicandoUnita(false);
    }
  };

  const handleSelectUnita = async (opt: ComboboxOption) => {
    setUnitaSearch('');
    setUnitaId(opt.value);
    const ok = await applicaUnitaATutteLeRighe(opt.value);
    if (ok && user) {
      setUltimaUO(user.id, opt.value);
    }
  };

  const calcolaEsitoPreview = (): string => {
    if (!ricezione) return 'in_verifica';
    const righe = ricezione.righe;
    if (righe.length === 0) return 'non_conforme';
    const tutteConformi = righe.every((r) => r.esito === 'conforme');
    const almenoUnaConforme = righe.some((r) => Number(r.quantita_conforme) > 0);
    if (tutteConformi) return 'conforme';
    if (almenoUnaConforme) return 'parzialmente_conforme';
    return 'non_conforme';
  };

  const righeConformi = ricezione
    ? ricezione.righe.filter((r) => Number(r.quantita_conforme) > 0)
    : [];
  const unitaMancante = righeConformi.length > 0 && !unitaId;

  const handleChiudi = async () => {
    if (unitaMancante) return;
    setClosing(true);
    // Safeguard: garantisce che le righe riflettano la UO di testata anche
    // se l'utente non ha mai toccato il selettore (default precompilato).
    if (unitaId) {
      const ok = await applicaUnitaATutteLeRighe(unitaId);
      if (!ok) {
        setClosing(false);
        return;
      }
    }
    const res = await fetch(`/api/v1/ordini/ricezioni/${ricezioneId}/chiudi`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.ok) {
      setShowConfirm(false);
      await fetchData();
    }
    setClosing(false);
  };

  if (loading || !ricezione) {
    return <div className="p-6 text-muted-foreground">Caricamento…</div>;
  }

  const chiusa = ricezione.stato !== 'in_verifica';

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-xl font-outfit font-semibold text-foreground">
            Ricezione merce
          </h1>
          <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
            <StatoRicezioneBadge stato={ricezione.stato} />
            <span>{ricezione.data_ricezione}</span>
            {ricezione.numero_ddt && <span>DDT: {ricezione.numero_ddt}</span>}
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/ordini/${ricezione.ordine_id}`)}>
            Torna all'ordine
          </Button>
          {!chiusa && (
            <Button
              onClick={() => setShowConfirm(true)}
              disabled={unitaMancante}
              title={
                unitaMancante
                  ? "Seleziona il negozio / unita organizzativa prima di chiudere"
                  : undefined
              }
            >
              Chiudi ricezione
            </Button>
          )}
        </div>
      </div>

      {!chiusa && (
        <Card className="p-4 space-y-2">
          <Label htmlFor="unita_organizzativa">
            Negozio / Unita' organizzativa (applicata a tutte le righe) *
          </Label>
          <div className="max-w-md">
            <Combobox
              value={(() => {
                const sel = unita.find((u) => u.id === unitaId);
                return sel ? sel.nome : unitaSearch;
              })()}
              onInputChange={(v: string) => {
                setUnitaSearch(v);
                if (unitaId) setUnitaId('');
              }}
              onSelect={handleSelectUnita}
              options={unita
                .filter((u) => {
                  const q = unitaSearch.trim().toLowerCase();
                  if (!q) return true;
                  return (
                    u.nome.toLowerCase().includes(q) ||
                    (u.codice ?? '').toLowerCase().includes(q)
                  );
                })
                .map<ComboboxOption>((u) => ({
                  value: u.id,
                  label: u.nome,
                  sublabel: u.codice ?? u.tipo,
                }))}
              placeholder="Cerca negozio / unita organizzativa..."
              loading={loadingUnita || applicandoUnita}
            />
          </div>
          {unitaMancante && (
            <p className="text-xs text-destructive">
              Campo obbligatorio: seleziona il negozio a cui attribuire le
              righe conformi prima di chiudere la ricezione.
            </p>
          )}
          {unitaError && (
            <p className="text-xs text-destructive">{unitaError}</p>
          )}
          {unitaFetchError && (
            <p className="text-xs text-destructive">
              Impossibile caricare i negozi. Ricarica la pagina.
            </p>
          )}
        </Card>
      )}

      <Card className="overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border bg-muted/50">
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Riga</th>
              <th className="text-right px-3 py-2 text-muted-foreground font-medium">Attesa</th>
              <th className="text-right px-3 py-2 text-muted-foreground font-medium">Ricevuta</th>
              <th className="text-right px-3 py-2 text-muted-foreground font-medium">Conforme</th>
              <th className="text-right px-3 py-2 text-muted-foreground font-medium">Danneg.</th>
              <th className="text-right px-3 py-2 text-muted-foreground font-medium">Manc.</th>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Lotto</th>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Scadenza</th>
              <th className="text-left px-3 py-2 text-muted-foreground font-medium">Esito</th>
            </tr>
          </thead>
          <tbody>
            {ricezione.righe.map((r, i) => (
              <tr key={r.id} className="border-b border-border">
                <td className="px-3 py-2 font-medium">#{i + 1}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {qty(r.quantita_attesa)}
                </td>
                <td className="px-3 py-2">
                  {chiusa ? (
                    <span className="text-right tabular-nums block">
                      {qty(r.quantita_ricevuta)}
                    </span>
                  ) : (
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={r.quantita_ricevuta}
                      onChange={(e: any) => aggiornaRiga(r.id, { quantita_ricevuta: Number(e.target.value) })}
                      className="w-20 text-right"
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {chiusa ? (
                    <span className="text-right tabular-nums block">
                      {qty(r.quantita_conforme)}
                    </span>
                  ) : (
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={r.quantita_conforme}
                      onChange={(e: any) => aggiornaRiga(r.id, { quantita_conforme: Number(e.target.value) })}
                      className="w-20 text-right"
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {chiusa ? (
                    <span className="text-right tabular-nums block">
                      {qty(r.quantita_danneggiata)}
                    </span>
                  ) : (
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={r.quantita_danneggiata}
                      onChange={(e: any) => aggiornaRiga(r.id, { quantita_danneggiata: Number(e.target.value) })}
                      className="w-20 text-right"
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {chiusa ? (
                    <span className="text-right tabular-nums block">
                      {qty(r.quantita_mancante)}
                    </span>
                  ) : (
                    <Input
                      type="number"
                      min="0"
                      step="1"
                      value={r.quantita_mancante}
                      onChange={(e: any) => aggiornaRiga(r.id, { quantita_mancante: Number(e.target.value) })}
                      className="w-20 text-right"
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {chiusa ? (
                    r.lotto || '—'
                  ) : (
                    <Input
                      value={r.lotto || ''}
                      onChange={(e: any) => aggiornaRiga(r.id, { lotto: e.target.value })}
                      className="w-24"
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {chiusa ? (
                    r.scadenza_prodotto || '—'
                  ) : (
                    <Input
                      type="date"
                      value={r.scadenza_prodotto || ''}
                      onChange={(e: any) => aggiornaRiga(r.id, { scadenza_prodotto: e.target.value })}
                      className="w-32"
                    />
                  )}
                </td>
                <td className="px-3 py-2">
                  {chiusa ? (
                    <StatoRicezioneBadge stato={r.esito} />
                  ) : (
                    <select
                      value={r.esito}
                      onChange={(e) => aggiornaRiga(r.id, { esito: e.target.value })}
                      className="rounded border border-border bg-card text-foreground px-2 py-1 text-xs"
                    >
                      <option value="in_verifica">In verifica</option>
                      <option value="conforme">Conforme</option>
                      <option value="parzialmente_conforme">Parz. conforme</option>
                      <option value="non_conforme">Non conforme</option>
                    </select>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Modale conferma chiusura */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <Card className="p-6 w-96 space-y-4">
            <h3 className="text-lg font-outfit font-semibold text-foreground">
              Chiudi ricezione
            </h3>
            <div className="text-sm">
              <span className="text-muted-foreground">Esito calcolato: </span>
              <StatoRicezioneBadge stato={calcolaEsitoPreview()} />
            </div>
            <p className="text-sm text-muted-foreground">
              Questa azione è irreversibile. Confermi la chiusura della ricezione?
            </p>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setShowConfirm(false)}>
                Annulla
              </Button>
              <Button onClick={handleChiudi} disabled={closing}>
                {closing ? 'Chiusura…' : 'Conferma chiusura'}
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
